import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CreditCard, LogIn as LogInIcon, LogOut as LogOutIcon, Printer, Plus } from "lucide-react";

export default function CheckIn() {
    const { activeHotelId } = useAuth();
    const loc = useLocation();
    const searchParams = new URLSearchParams(loc.search);
    const resIdFromUrl = searchParams.get("res");

    const [reservations, setReservations] = useState([]);
    const [selectedId, setSelectedId] = useState(resIdFromUrl || "");
    const [res, setRes] = useState(null);
    const [payment, setPayment] = useState({ amount: 0, method: "cash", reference: "" });
    const [charge, setCharge] = useState({ description: "", category: "mini_bar", amount: 0 });

    const load = async () => {
        if (!activeHotelId) return;
        const { data } = await api.get(`/reservations?hotel_id=${activeHotelId}`);
        setReservations(data.filter(r => ["confirmed", "checked_in"].includes(r.status)));
    };

    const loadOne = async (id) => {
        if (!id) return setRes(null);
        try {
            const { data } = await api.get(`/reservations/${id}`);
            setRes(data);
            // Pre-fill payment amount with outstanding balance for UX
            if (data?.balance > 0) {
                setPayment(prev => ({ ...prev, amount: data.balance }));
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { load(); }, [activeHotelId]);
    useEffect(() => { loadOne(selectedId); }, [selectedId]);

    const doCheckIn = async () => {
        try {
            await api.post(`/reservations/${selectedId}/checkin`, { id_verified: true, deposit: 0, key_card_issued: true });
            toast.success("Checked in successfully");
            load(); loadOne(selectedId);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doCheckOut = async () => {
        try {
            await api.post(`/reservations/${selectedId}/checkout`);
            toast.success("Checked out. Room set to Dirty.");
            load(); loadOne(selectedId);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const addPayment = async () => {
        try {
            await api.post(`/reservations/${selectedId}/payments`, { ...payment, amount: Number(payment.amount) });
            toast.success("Payment recorded");
            setPayment({ amount: 0, method: "cash", reference: "" });
            loadOne(selectedId);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const addCharge = async () => {
        try {
            await api.post(`/reservations/${selectedId}/charges`, { ...charge, amount: Number(charge.amount) });
            toast.success("Charge added");
            setCharge({ description: "", category: "mini_bar", amount: 0 });
            loadOne(selectedId);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Check-In / Check-Out" subtitle="Guest arrival, departure, folio">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: reservations list */}
                <Card className="p-4 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Active</div>
                    <div className="space-y-1">
                        {reservations.map(r => (
                            <button key={r.id} onClick={() => setSelectedId(r.id)}
                                className={`w-full text-left p-3 rounded-sm border ${selectedId === r.id ? "bg-slate-50 border-[#0A1C3E]" : "border-transparent hover:bg-slate-50"}`}
                                data-testid={`checkin-select-${r.id}`}>
                                <div className="flex justify-between items-center">
                                    <div className="text-sm font-medium text-slate-900">{r.guest_name}</div>
                                    <span className={`status-badge ${r.status === "checked_in" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-indigo-100 text-indigo-800 border-indigo-200"}`}>
                                        {r.status.replace("_", " ")}
                                    </span>
                                </div>
                                <div className="text-xs text-slate-500 mt-1">{r.check_in_date?.slice(0, 10)} → {r.check_out_date?.slice(0, 10)}</div>
                            </button>
                        ))}
                        {reservations.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No active bookings</div>}
                    </div>
                </Card>

                {/* Right: folio */}
                <div className="lg:col-span-2">
                    {res ? (
                        <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
                            <div className="p-6 border-b border-slate-200 bg-slate-50 flex justify-between items-start">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Guest Folio</div>
                                    <h2 className="text-xl font-display font-bold text-slate-900 mt-1">{res.guest_name}</h2>
                                    <div className="text-xs text-slate-500 mt-1">{res.guest_email} · {res.guest_phone}</div>
                                </div>
                                <div className="flex gap-2">
                                    {res.status === "confirmed" && (
                                        <Button onClick={doCheckIn} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="do-checkin">
                                            <LogInIcon className="w-4 h-4 mr-1" /> Check In
                                        </Button>
                                    )}
                                    {res.status === "checked_in" && (
                                        <Button onClick={doCheckOut} disabled={res.balance > 0.01} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="do-checkout">
                                            <LogOutIcon className="w-4 h-4 mr-1" /> Check Out
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="p-6 grid grid-cols-4 gap-4 border-b border-slate-100">
                                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Subtotal</div>
                                    <div className="font-mono font-semibold text-slate-900 mt-1">₹{Number(res.subtotal).toLocaleString()}</div></div>
                                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Tax ({res.tax_percent}%)</div>
                                    <div className="font-mono font-semibold text-slate-900 mt-1">₹{Number(res.tax).toLocaleString()}</div></div>
                                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                                    <div className="font-mono font-bold text-lg text-slate-900 mt-1">₹{Number(res.total).toLocaleString()}</div></div>
                                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Balance</div>
                                    <div className={`font-mono font-bold text-lg mt-1 ${res.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>₹{Number(res.balance).toLocaleString()}</div></div>
                            </div>

                            <Tabs defaultValue="charges" className="p-6">
                                <TabsList className="bg-slate-100">
                                    <TabsTrigger value="charges" data-testid="tab-charges">Charges</TabsTrigger>
                                    <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
                                    <TabsTrigger value="add-charge" data-testid="tab-add-charge">Add Charge</TabsTrigger>
                                    <TabsTrigger value="collect" data-testid="tab-collect">Collect Payment</TabsTrigger>
                                </TabsList>

                                <TabsContent value="charges" className="mt-4">
                                    <table className="w-full">
                                        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                                            <tr><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-right">Amount</th></tr>
                                        </thead>
                                        <tbody>
                                            {(res.charges || []).map((c, i) => (
                                                <tr key={i} className="border-b border-slate-100">
                                                    <td className="px-3 py-2 text-sm">{c.description}</td>
                                                    <td className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">{c.category?.replace(/_/g, " ")}</td>
                                                    <td className="px-3 py-2 text-sm font-mono text-right">₹{Number(c.amount).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </TabsContent>

                                <TabsContent value="payments" className="mt-4">
                                    <table className="w-full">
                                        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                                            <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Method</th><th className="px-3 py-2 text-left">Ref</th><th className="px-3 py-2 text-right">Amount</th></tr>
                                        </thead>
                                        <tbody>
                                            {(res.payments || []).map((p, i) => (
                                                <tr key={i} className="border-b border-slate-100">
                                                    <td className="px-3 py-2 text-xs text-slate-500">{p.date?.slice(0, 16).replace("T", " ")}</td>
                                                    <td className="px-3 py-2 text-sm uppercase">{p.method}</td>
                                                    <td className="px-3 py-2 text-xs text-slate-500">{p.reference}</td>
                                                    <td className="px-3 py-2 text-sm font-mono text-right text-emerald-700">₹{Number(p.amount).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                            {(!res.payments || res.payments.length === 0) && <tr><td colSpan="4" className="text-center py-4 text-sm text-slate-500">No payments yet</td></tr>}
                                        </tbody>
                                    </table>
                                </TabsContent>

                                <TabsContent value="add-charge" className="mt-4">
                                    <div className="grid grid-cols-2 gap-3 max-w-xl">
                                        <div><Label className="text-xs uppercase tracking-wider">Description</Label>
                                            <Input value={charge.description} onChange={e => setCharge({ ...charge, description: e.target.value })} data-testid="charge-desc" /></div>
                                        <div><Label className="text-xs uppercase tracking-wider">Category</Label>
                                            <Select value={charge.category} onValueChange={v => setCharge({ ...charge, category: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="mini_bar">Mini Bar</SelectItem>
                                                    <SelectItem value="laundry">Laundry</SelectItem>
                                                    <SelectItem value="restaurant">Restaurant</SelectItem>
                                                    <SelectItem value="damage">Damage</SelectItem>
                                                    <SelectItem value="other">Other</SelectItem>
                                                </SelectContent>
                                            </Select></div>
                                        <div><Label className="text-xs uppercase tracking-wider">Amount</Label>
                                            <Input type="number" value={charge.amount} onChange={e => setCharge({ ...charge, amount: e.target.value })} data-testid="charge-amount" /></div>
                                        <div className="col-span-2">
                                            <Button onClick={addCharge} disabled={!charge.description || !charge.amount} className="bg-[#0A1C3E]" data-testid="charge-submit">
                                                <Plus className="w-4 h-4 mr-1" /> Add Charge
                                            </Button>
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent value="collect" className="mt-4">
                                    <div className="grid grid-cols-2 gap-3 max-w-xl">
                                        <div><Label className="text-xs uppercase tracking-wider">Amount</Label>
                                            <Input type="number" value={payment.amount} onChange={e => setPayment({ ...payment, amount: e.target.value })} data-testid="payment-amount" /></div>
                                        <div><Label className="text-xs uppercase tracking-wider">Method</Label>
                                            <Select value={payment.method} onValueChange={v => setPayment({ ...payment, method: v })}>
                                                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="cash">Cash</SelectItem>
                                                    <SelectItem value="card">Card</SelectItem>
                                                    <SelectItem value="upi">UPI</SelectItem>
                                                    <SelectItem value="bank">Bank Transfer</SelectItem>
                                                    <SelectItem value="wallet">Wallet</SelectItem>
                                                </SelectContent>
                                            </Select></div>
                                        <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Reference / Note</Label>
                                            <Input value={payment.reference} onChange={e => setPayment({ ...payment, reference: e.target.value })} /></div>
                                        <div className="col-span-2">
                                            <Button onClick={addPayment} disabled={!payment.amount} className="bg-[#0A1C3E]" data-testid="payment-submit">
                                                <CreditCard className="w-4 h-4 mr-1" /> Record Payment
                                            </Button>
                                        </div>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </Card>
                    ) : (
                        <Card className="p-12 text-center border border-dashed border-slate-300 rounded-md">
                            <p className="text-slate-500 text-sm">Select a reservation to view guest folio</p>
                        </Card>
                    )}
                </div>
            </div>
        </AppShell>
    );
}
